import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { initTheme } from "./lib/theme";
import { BRAND, applyBrandToDocument } from "./lib/branding";
import PWAUpdateWatcher from "./components/PWAUpdateWatcher";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import QuotaPage from "./pages/QuotaPage";
import EventsPage from "./pages/EventsPage";
import CalendarPage from "./pages/CalendarPage";
import CabinsPage from "./pages/CabinsPage";
import MembersPage from "./pages/MembersPage";
import SkupinePage from "./pages/SkupinePage";
import VezicePage from "./pages/VezicePage";
import LisjakPage from "./pages/LisjakPage";
import ProfilePage from "./pages/ProfilePage";
import SuperAdminProfile from "./pages/SuperAdminProfile";
import DemoLanding from "./pages/DemoLanding";
import AnnouncementsPage from "./pages/AnnouncementsPage";
import HoursApprovalPage from "./pages/HoursApprovalPage";
import MemberWeaponsPage from "./pages/MemberWeaponsPage";
import SMSPage from "./pages/SMSPage";
import AccessLogsPage from "./pages/AccessLogsPage";
import HuntingMapPage from "./pages/HuntingMapPage";
import CameraPage from "./pages/CameraPage";
import AIJawPage from "./pages/AIJawPage";
import FilesPage from "./pages/FilesPage";
import AttendanceArchivePage from "./pages/AttendanceArchivePage";
import MessagesPage from "./pages/MessagesPage";
import StatisticsPage from "./pages/StatisticsPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import SendEmailPage from "./pages/SendEmailPage";
import TrainingPage from "./pages/TrainingPage";
import ExportsPage from "./pages/ExportsPage";
import RolesPage from "./pages/RolesPage";
import RegisterLDPage from "./pages/RegisterLDPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import LandingPage from "./pages/LandingPage";
import DnevnikPage from "./pages/DnevnikPage";
import MissedShotsPage from "./pages/MissedShotsPage";
import JubilejiPage from "./pages/JubilejiPage";
import AplikacijaPage from "./pages/AplikacijaPage";
import PodporaPage from "./pages/PodporaPage";
import ZasebnostPage from "./pages/ZasebnostPage";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// -- Safe storage wrapper --
// V opaque-origin iframe-u (sandbox brez allow-same-origin) `localStorage` vrže SecurityError.
// Nadomestimo z in-memory Map, da aplikacija zna delovati brez trajnega shranjevanja.
const _memoryStore = new Map();
export const safeStorage = {
  getItem(key) {
    try { return window.localStorage.getItem(key); }
    catch (_) { return _memoryStore.has(key) ? _memoryStore.get(key) : null; }
  },
  setItem(key, value) {
    try { window.localStorage.setItem(key, value); }
    catch (_) { _memoryStore.set(key, String(value)); }
  },
  removeItem(key) {
    try { window.localStorage.removeItem(key); }
    catch (_) { _memoryStore.delete(key); }
  },
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
// V "preview" iframe načinu (na landing strani) uporabimo drug localStorage ključ,
// da avtomatski demo login znotraj iframe-a ne prepiše žetona uporabnika v parent oknu.
export const IS_IFRAME = typeof window !== "undefined" && window.self !== window.top;
export const IS_PREVIEW = (() => {
  if (typeof window === "undefined") return false;
  const p = new URLSearchParams(window.location.search);
  // "share_auth=1" izrecno pove, da iframe ŽELI deliti žeton s parent-om (npr. FAB v demo).
  if (p.get("share_auth") === "1") return false;
  return p.get("preview") === "1" || IS_IFRAME;
})();
export const TOKEN_KEY = IS_PREVIEW ? "preview_token" : "token";
export const LD_NAZIV_KEY = IS_PREVIEW ? "preview_ld_naziv" : "ld_naziv";

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(safeStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = safeStorage.getItem(TOKEN_KEY);
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          const userData = response.data;
          // Restore LD naziv from localStorage or fetch
          const cachedLdNaziv = safeStorage.getItem(LD_NAZIV_KEY);
          if (cachedLdNaziv && userData?.ld_id) {
            userData.ld_naziv = cachedLdNaziv;
          } else if (userData?.ld_id) {
            try {
              const r = await axios.get(`${API}/lovske-druzine/list`);
              const ld = (r.data || []).find(l => l.id === userData.ld_id);
              if (ld) {
                userData.ld_naziv = ld.naziv;
                safeStorage.setItem(LD_NAZIV_KEY, ld.naziv);
              }
            } catch (_) { /* ignore ld naziv fetch fail */ }
          }
          setUser(userData);
          setToken(savedToken);
        } catch (error) {
          safeStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (lovskaIzkaznica, stevilkaClana, ldId = null, opts = {}) => {
    try {
      let newToken, userData;
      if (opts.skipRequest && opts.prefetched) {
        newToken = opts.prefetched.token;
        userData = opts.prefetched.user;
      } else {
        const payload = { lovska_izkaznica: lovskaIzkaznica, stevilka_clana: stevilkaClana };
        if (ldId) payload.ld_id = ldId;
        const response = await axios.post(`${API}/auth/login`, payload);
        // Multi-LD case: return the options for caller
        if (response.data.needs_ld_choice) {
          return { needs_ld_choice: true, options: response.data.options };
        }
        newToken = response.data.token;
        userData = response.data.user;
      }
      safeStorage.setItem(TOKEN_KEY, newToken);
      if (userData?.ld_id) safeStorage.setItem("ld_id", userData.ld_id);
      if (userData?.ld_naziv) safeStorage.setItem(LD_NAZIV_KEY, userData.ld_naziv);
      setToken(newToken);
      setUser(userData);
      if (!opts.skipRequest) toast.success("Prijava uspešna!");
      return true;
    } catch (error) {
      if (!opts.skipRequest) toast.error(error.response?.data?.detail || "Napaka pri prijavi");
      return false;
    }
  };

  const logout = () => {
    safeStorage.removeItem(TOKEN_KEY);
    safeStorage.removeItem("ld_id");
    safeStorage.removeItem(LD_NAZIV_KEY);
    safeStorage.removeItem("sa_impersonation_ld");
    setToken(null);
    setUser(null);
    toast.success("Odjava uspešna");
  };

  // SA impersonate: swap tokens, cache LD naziv, refetch /auth/me
  const impersonate = async (ldId, password) => {
    try {
      const res = await axios.post(`${API}/superadmin/lovske-druzine/${ldId}/impersonate`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { token: newToken, ld_naziv, ld_koda } = res.data;
      safeStorage.setItem(TOKEN_KEY, newToken);
      safeStorage.setItem("ld_id", ldId);
      if (ld_naziv) safeStorage.setItem(LD_NAZIV_KEY, ld_naziv);
      safeStorage.setItem("sa_impersonation_ld", JSON.stringify({ ld_id: ldId, ld_naziv, ld_koda }));
      setToken(newToken);
      // Refetch user
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${newToken}` } });
      setUser({ ...me.data, ld_naziv });
      toast.success(`Vstopili ste kot predsednik LD ${ld_naziv}`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri impersonaciji");
      return false;
    }
  };

  const returnFromImpersonate = async () => {
    try {
      const res = await axios.post(`${API}/superadmin/return-from-impersonate`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { token: freshToken, user: sa } = res.data;
      safeStorage.setItem(TOKEN_KEY, freshToken);
      safeStorage.removeItem("ld_id");
      safeStorage.removeItem(LD_NAZIV_KEY);
      safeStorage.removeItem("sa_impersonation_ld");
      setToken(freshToken);
      setUser(sa);
      toast.success("Vrnili ste se v Super-admin");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri vračilu iz impersonacije");
      return false;
    }
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (!Array.isArray(roles)) roles = [roles];
    // Super admin ONLY sees super_admin-scoped routes
    if (user.vloga === "super_admin") return roles.includes("super_admin");
    // super_admin route is exclusive to super_admin - block everyone else
    if (roles.length === 1 && roles[0] === "super_admin") return false;
    if (user.vloga === "administrator") return true;
    return roles.includes(user.vloga) || user.vloga === "predsednik";
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasRole, impersonate, returnFromImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to={user?.vloga === "super_admin" ? "/super-admin" : "/dashboard"} replace />;
  }

  return children;
};

// Profile route dispatcher: SA (ne-impersoniran) → SuperAdminProfile, drugače → ProfilePage
const ProfileDispatcher = () => {
  const { user } = useAuth();
  if (user?.vloga === "super_admin" && !user?.impersonating) {
    return <SuperAdminProfile />;
  }
  return <ProfilePage />;
};

// Main App Routes
const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Nalaganje...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.vloga === "super_admin" ? "/super-admin" : "/dashboard"} /> : <LoginPage />} />
      <Route path="/register-ld" element={<RegisterLDPage />} />
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/uvodna" element={<LandingPage />} />
      <Route path="/aplikacija" element={<AplikacijaPage />} />
      <Route path="/podpora" element={<PodporaPage />} />
      <Route path="/zasebnost" element={<ZasebnostPage />} />
      {!user && (
        <Route
          path="/"
          element={BRAND.showLandingPage ? <LandingPage /> : <Navigate to="/login" replace />}
        />
      )}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to={user?.vloga === "super_admin" ? "/super-admin" : "/dashboard"} replace />} />
        <Route path="dashboard" element={user?.vloga === "super_admin" ? <Navigate to="/super-admin" replace /> : <DashboardPage />} />
        <Route path="obvestila" element={<AnnouncementsPage />} />
        <Route path="plan-odstrela" element={<QuotaPage />} />
        <Route path="koledar" element={<CalendarPage />} />
        <Route path="dogodki" element={<EventsPage />} />
        <Route path="koce" element={<CabinsPage />} />
        <Route path="clani" element={<MembersPage />} />
        <Route path="ure" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "upravnik_lovske_koce", "skupinovodja"]}>
            <HoursApprovalPage />
          </ProtectedRoute>
        } />
        <Route path="orozje" element={<MemberWeaponsPage />} />
        <Route path="zgreseni-streli" element={<MissedShotsPage />} />
        <Route path="izobrazevanje" element={<TrainingPage />} />
        <Route path="izvozi" element={<ExportsPage />} />
        <Route path="vloge" element={<RolesPage />} />
        <Route path="skupine" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik", "administrator", "super_admin"]}>
            <SkupinePage />
          </ProtectedRoute>
        } />
        <Route path="vezice" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik", "administrator", "super_admin"]}>
            <VezicePage />
          </ProtectedRoute>
        } />
        <Route path="lisjak" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik", "administrator", "super_admin"]}>
            <LisjakPage />
          </ProtectedRoute>
        } />
        <Route path="sms" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik", "blagajnik", "skupinovodja"]}>
            <SMSPage />
          </ProtectedRoute>
        } />
        <Route path="dostopi" element={
          <ProtectedRoute roles={["predsednik", "tajnik"]}>
            <AccessLogsPage />
          </ProtectedRoute>
        } />
        <Route path="karta" element={<HuntingMapPage />} />
        <Route path="kamere" element={<CameraPage />} />
        <Route path="ai-ocena" element={<AIJawPage />} />
        <Route path="datoteke" element={<FilesPage />} />
        <Route path="sporocila" element={<MessagesPage />} />
        <Route path="arhiv-prisotnosti" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik"]}>
            <AttendanceArchivePage />
          </ProtectedRoute>
        } />
        <Route path="statistike" element={
          <ProtectedRoute roles={["predsednik", "tajnik", "gospodar"]}>
            <StatisticsPage />
          </ProtectedRoute>
        } />
        <Route path="jubileji" element={
          <ProtectedRoute roles={["predsednik", "gospodar", "tajnik", "administrator", "super_admin"]}>
            <JubilejiPage />
          </ProtectedRoute>
        } />
        <Route path="email-nastavitve" element={
          <ProtectedRoute roles={["predsednik", "tajnik"]}>
            <EmailSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="dnevnik" element={
          <ProtectedRoute roles={["predsednik", "tajnik", "gospodar", "blagajnik", "administrator", "super_admin"]}>
            <DnevnikPage />
          </ProtectedRoute>
        } />
        <Route path="poslji-email" element={
          <ProtectedRoute roles={["predsednik", "tajnik", "gospodar"]}>
            <SendEmailPage />
          </ProtectedRoute>
        } />
        <Route path="profil" element={<ProfileDispatcher />} />
        <Route path="narocnina" element={<SubscriptionPage />} />
        <Route path="super-admin" element={
          <ProtectedRoute roles={["super_admin"]}>
            <SuperAdminPage />
          </ProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<Navigate to={user?.vloga === "super_admin" ? "/super-admin" : "/dashboard"} replace />} />
    </Routes>
  );
};

function App() {
  useEffect(() => {
    initTheme();
    applyBrandToDocument();
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <PWAUpdateWatcher />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
